import { useEffect, useState, useMemo } from 'react';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { DBO_U, DBO_V, DBO_REQ, DBO_TM, DBO_ARR } from '@op/utils/format';
import {
  commonFilter,
  EffectiveTimeList,
  KeyMetricsChart,
  RichText,
  RichTextResp,
} from '../type';
import {
  UseRichTextProps,
  UseEffectiveTimeListProps,
  EffectiveTimeList_API_Resp,
  UseCoreKeyMetricsProps,
} from './type';
import {
  getWeekly_OverView_EffectiveTimeList,
  getWeekly_OverView_getRichText,
  getWeekly_OverView_editRichText,
  getWeekly_OverView_KeyMetricsTable,
  getWeekly_OverView_KeyMetricsChart,
  getWeekly_OverView_ChannelRank,
} from '@op/services';
import { TRequestBackType } from '@op/utils/requestUtil';

// 主页顶层filter-时间下拉框
export function useEffectiveTimeList({ gameInfo }: UseEffectiveTimeListProps) {
  const { wrapperCancelRequest, loading } = useCancelRequestHook({
    defaultLoading: true,
  });
  const [effectiveTime, setEffectiveTime] = useState<string>();
  const [effectiveTimeList, setEffectiveTimeList] = useState<
    EffectiveTimeList[]
  >([]);

  const { filter } = useMemo<{ filter: commonFilter | null }>(() => {
    if (!DBO_V.is_empty(effectiveTime)) {
      const item = effectiveTimeList.find(item => item.value === effectiveTime);
      if (item) {
        const timeFilter = item.effectiveTime;
        return {
          filter: { ...timeFilter, date_type: 'weekly', ...gameInfo },
        };
      }
    }
    return { filter: null };
  }, [effectiveTime]);

  function request() {
    wrapperCancelRequest(getWeekly_OverView_EffectiveTimeList, gameInfo)
      .then((data: EffectiveTimeList_API_Resp[]) => {
        const timesList = data.map(item => {
          const text = DBO_TM.timeTitle({ ...item, date_type: 'daily' });

          const effectiveTime = {
            start_time: item.start_time,
            end_time: item.end_time,
          };

          return { text, value: text, effectiveTime };
        });
        setEffectiveTimeList(timesList);
        setEffectiveTime(timesList[0].value);
      })
      .catch(e => console.log(e));
  }

  useEffect(() => {
    request();
  }, []);

  return {
    filter,
    loading,
    effectiveTime,
    setEffectiveTime,
    effectiveTimeList,
  };
}

// 核心概览富文本
const summaryOptions: { [key: string]: { text: string; textCn: string } } = {
  overview: {
    text: 'Metrics Overview',
    textCn: '数据概览',
  },
  account: {
    text: 'Official Account Community Core Feedback',
    textCn: '官号社群核心反馈',
  },
  ugc: {
    text: 'UGC Content Insights',
    textCn: 'UGC内容洞察',
  },
};

export function useRichText({ filter }: UseRichTextProps) {
  const { wrapperCancelRequest, loading } = useCancelRequestHook({
    defaultLoading: true,
  });
  const [richText, setRichText] = useState<RichText[]>([]);

  function request() {
    wrapperCancelRequest(getWeekly_OverView_getRichText, {
      ...filter,
      summary_types: ['overview', 'account', 'ugc'],
    })
      .then((data: RichTextResp[]) => {
        const richTextList = (data || []).map(item => {
          const { summary, summary_type, id } = item || {};
          return {
            summary: summary || '',
            summaryCn: summary || '',
            key: summary_type,
            ...(summaryOptions[summary_type] || {}),
            value: String(id),
          };
        });

        setRichText(
          richTextList.sort(
            DBO_ARR.sortByMap(
              { overview: 0, account: 1, ugc: 2 },
              item => item.key,
            ),
          ),
        );
      })
      .catch(console.error);
  }

  useEffect(() => {
    if (!filter) return;

    setRichText([]);
    request();
  }, [filter]);

  function modifyRichText(params: any) {
    return getWeekly_OverView_editRichText({ data: params })
      .then(() => true)
      .catch(() => false);
  }

  return {
    richText,
    modifyRichText,
    loading,
  };
}

// 核心指标周度追踪-table
export function useCoreKeyMetrics({ filter }: UseCoreKeyMetricsProps) {
  const [data, setData] = useState<any[]>([]);
  const { wrapperCancelRequest, loading } = useCancelRequestHook();

  function request() {
    wrapperCancelRequest(getWeekly_OverView_KeyMetricsTable, filter)
      .then((data: any) => {
        const { list, this_week_time, last_week_time, avg_five_week_time } =
          data;

        const dataList = list.map((item: any) => {
          const { this_week, last_week, avg_five_week } = item;

          return {
            ...item,
            week_on_week: DBO_V.qoq(this_week, last_week),
            avg_five_week_on_week: DBO_V.qoq(this_week, avg_five_week),
            newTime: [
              this_week_time?.link_of_start_time,
              this_week_time?.link_of_end_time,
            ],
            last_week_time: [
              last_week_time?.link_of_start_time,
              last_week_time?.link_of_end_time,
            ],
            avg_five_week_time: [
              avg_five_week_time?.link_of_start_time,
              avg_five_week_time?.link_of_end_time,
            ],
          };
        });
        setData(dataList);
      })
      .catch(_err => {});
  }

  useEffect(() => {
    if (!filter) return;

    request();
  }, [filter]);

  function download() {
    DBO_U.download({
      fileName: 'Weekly Tracking of Key Metrics',
      key: 'WeeklyReportOverviewKeyMetrics',
      filter,
      data,
    });
  }

  return { data, loading, download };
}

// 核心指标周度追踪-Chart
export function useCoreKeyMetricsChart({ metric, filter }: KeyMetricsChart) {
  const [chartData, setData] = useState<TRequestBackType>({ isEmpty: true });
  const { wrapperCancelRequest, loading } = useCancelRequestHook({
    clear: () => {
      setData({ isEmpty: true });
    },
  });

  function request() {
    wrapperCancelRequest(getWeekly_OverView_KeyMetricsChart, {
      ...filter,
      metric,
    })
      .then((data: any) => {
        const { start_time, end_time, list, avg_five_week } = data || {};

        if (!list || list.length === 0) {
          setData({ isEmpty: true });
          return;
        }
        const info = DBO_REQ.moreKeys({
          timeline: list,
          timeline_of_start_time: start_time,
          timeline_of_end_time: end_time,
          timeKey: 'time',
          filter: {
            date_type: 'weekly',
          },
        });

        const { seriesObj } = info;
        if (seriesObj && avg_five_week !== undefined) {
          seriesObj.Past_5_week_Average = new Array(6).fill(avg_five_week);
          seriesObj.val_and_5week_Rate = seriesObj.val.map(item =>
            DBO_V.qoq(item, avg_five_week),
          );
        }

        setData(info);
      })
      .catch(_err => {});
  }

  useEffect(() => {
    request();
  }, [metric, filter]);

  return { chartData, loading };
}

// 渠道排名周度追踪
export function useChannelRank({ filter }: UseCoreKeyMetricsProps) {
  const [data, setData] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('all');
  const { wrapperCancelRequest, loading } = useCancelRequestHook();

  function request() {
    if (!filter) return;
    const { date_type, ...remainingFilter } = filter;

    wrapperCancelRequest(getWeekly_OverView_ChannelRank, {
      ...remainingFilter,
      channels: activeTab === 'all' ? [] : [activeTab],
    })
      .then((data: any) => {
        const {
          list,
          start_time,
          end_time,
          link_of_start_time,
          link_of_end_time,
        } = data;

        const dataList = list.map((item: any) => {
          const { game, list, old_list, index, old_index } = item;

          const obj = DBO_REQ.qoq({ list, otherList: old_list });
          return {
            ...game,
            ...obj,
            index,
            old_index,
            rankChange: old_index - index,
            newTime: [start_time, end_time],
            last_week_time: [link_of_start_time, link_of_end_time],
          };
        });
        setData(dataList);
      })
      .catch(_err => {});
  }

  useEffect(() => {
    setData([]);
    request();
  }, [filter, activeTab]);

  return {
    data,
    activeTab,
    setActiveTab,
    loading,
  };
}
